<script setup lang="ts">
import { useHead, useRoute, useRouter } from '#app';
import { orderBlogPostsByFeaturedSlugs, useBlog, type BlogListConfig } from '@/composables/useBlog';
import { provideSeoDetailLanguagePaths } from '@/composables/useSeoDetailLanguageNavigation';
import { useSeoLocaleAvailability } from '@/composables/useSeoLocaleAvailability';
import { applyBlogPostsListLocale } from '@/lib/blog-locale';
import {
  SITE_LOCALES,
  absoluteLocalizedUrl,
  hreflangFor,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';
import AppNav from '@/components/AppNav.vue';
import AppFooter from '@/components/AppFooter.vue';
import FeaturesRoles from '@/components/features/FeaturesRoles.vue';
import { computed, ref } from 'vue';
import { i18n, t } from '@/i18n';

interface BlogCategory {
  title: string;
}

interface BlogAuthor {
  name: string;
  avatar?: string;
}

interface BlogPost {
  _id: string;
  slug: string;
  title: string;
  excerpt?: any[]; // Portable Text array
  image?: string;
  publishedAt?: string;
  categories?: BlogCategory[];
  category?: string;
  tags?: string[];
  readTime?: number;
  author?: BlogAuthor;
}

const route = useRoute();
const router = useRouter();
const siteLocale = siteLocaleFromRouteParam(route.params.locale);
const { fetchBlogListConfig, fetchBlogPosts } = useBlog();
const { fetchLocaleAvailability } = useSeoLocaleAvailability();
const blogIndexPath = computed(() => localizedPath(siteLocale, '/blog'));
const blogPagePath = (page: number) => localizedPath(siteLocale, `/blog/page/${page}`);
const blogPostPath = (slug: string) => localizedPath(siteLocale, `/blog/${slug}`);

// Get page number from route params: /blog/page/2 -> num = "2"
const pageNumber = parseInt(route.params.num as string, 10);

if (isNaN(pageNumber) || pageNumber < 2) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page not found',
  });
}

const postsPerPage = 9;
const currentPage = ref(pageNumber);

const { data: blogListConfig } = await useAsyncData<BlogListConfig>(
  () => `blog-list-config-${siteLocale}`,
  () => fetchBlogListConfig(siteLocale),
  {
    server: true,
    lazy: false,
    default: () => ({
      language: siteLocale,
      exists: false,
      seoTitle: '',
      seoDescription: '',
      keywords: [],
      title: '',
      subtitle: '',
      featuredArticleSlugs: [],
    }),
  }
);

const { data: allPosts } = await useAsyncData<BlogPost[]>(
  () => `blog-posts-${siteLocale}`,
  async () => {
    try {
      const data = await fetchBlogPosts(siteLocale);
      const localizedPosts = applyBlogPostsListLocale((data || []) as BlogPost[]);
      return orderBlogPostsByFeaturedSlugs(
        localizedPosts,
        blogListConfig.value.featuredArticleSlugs
      );
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      return [];
    }
  },
  {
    server: true,
    lazy: false,
    default: () => [],
  }
);
const { data: localeAvailability } = await useAsyncData(
  'blog-page-locale-availability',
  () => fetchLocaleAvailability('blog'),
  {
    server: true,
    lazy: false,
    default: () => ({
      section: 'blog' as const,
      locales: [],
      counts: { en: 0, ja: 0, ko: 0, es: 0 },
    }),
  }
);

const totalPosts = computed(() => allPosts.value?.length || 0);
const totalPages = computed(() => Math.ceil(totalPosts.value / postsPerPage));

if (totalPages.value === 0 || currentPage.value > totalPages.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page not found',
  });
}
const pageLocales = computed(() => {
  const locales = SITE_LOCALES.filter(
    (locale) =>
      Math.ceil((localeAvailability.value.counts[locale] || 0) / postsPerPage) >= currentPage.value
  );
  return locales.length > 0 ? locales : [siteLocale];
});
const languagePaths = computed(() =>
  Object.fromEntries(
    pageLocales.value.map((availableLocale) => [
      availableLocale,
      localizedPath(availableLocale, `/blog/page/${currentPage.value}`),
    ])
  )
);
provideSeoDetailLanguagePaths(languagePaths);

const startIndex = computed(() => (currentPage.value - 1) * postsPerPage);
const endIndex = computed(() => startIndex.value + postsPerPage);
const paginatedPosts = computed(() => {
  if (!allPosts.value) return [];
  return allPosts.value.slice(startIndex.value, endIndex.value);
});

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  if (page === 1) {
    router.push(blogIndexPath.value);
  } else {
    router.push(blogPagePath(page));
  }
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1);
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
}

const pageNumbers = computed(() => {
  const pages: (number | string)[] = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    if (current > 3) pages.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push('...');
    pages.push(total);
  }

  return pages;
});

const heroTitle = computed(
  () => blogListConfig.value.title || t('landingPages.blog.index.hero.title')
);
const heroSubtitle = computed(
  () => blogListConfig.value.subtitle || t('landingPages.blog.index.hero.subtitle')
);
const pageTitleBase = computed(
  () => blogListConfig.value.seoTitle || t('landingPages.blog.index.seo.title')
);
const pageDescription = computed(
  () => blogListConfig.value.seoDescription || t('landingPages.blog.index.seo.description')
);
const pageKeywords = computed(() => blogListConfig.value.keywords.join(', '));
const pageTitle = computed(() =>
  t('landingPages.blog.index.seo.page_title', {
    title: pageTitleBase.value,
    page: String(currentPage.value),
  })
);

const canonicalUrl = computed(() => {
  return absoluteLocalizedUrl(siteLocale, `/blog/page/${currentPage.value}`);
});

const prevUrl = computed(() => {
  if (currentPage.value > 1) {
    const prevPageNum = currentPage.value - 1;
    return prevPageNum === 1
      ? absoluteLocalizedUrl(siteLocale, '/blog')
      : absoluteLocalizedUrl(siteLocale, `/blog/page/${prevPageNum}`);
  }
  return null;
});

const nextUrl = computed(() => {
  if (currentPage.value < totalPages.value) {
    return absoluteLocalizedUrl(siteLocale, `/blog/page/${currentPage.value + 1}`);
  }
  return null;
});

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => {
  const links = [
    { rel: 'canonical', href: canonicalUrl.value },
    ...pageLocales.value.map((locale) => ({
      rel: 'alternate',
      hreflang: hreflangFor(locale),
      href: absoluteLocalizedUrl(locale, `/blog/page/${currentPage.value}`),
      key: `alternate-${locale}`,
    })),
    ...(pageLocales.value.includes('en')
      ? [
          {
            rel: 'alternate',
            hreflang: 'x-default',
            href: absoluteLocalizedUrl('en', `/blog/page/${currentPage.value}`),
            key: 'alternate-x-default',
          },
        ]
      : []),
  ];
  if (prevUrl.value) {
    links.push({ rel: 'prev', href: prevUrl.value });
  }
  if (nextUrl.value) {
    links.push({ rel: 'next', href: nextUrl.value });
  }

  return {
    title: pageTitle.value,
    htmlAttrs: { lang: siteLocale },
    meta: [
      { name: 'description', content: pageDescription.value },
      ...(pageKeywords.value ? [{ name: 'keywords', content: pageKeywords.value }] : []),
      { property: 'og:title', content: pageTitleBase.value },
      { property: 'og:description', content: pageDescription.value },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:locale', content: ogLocaleFor(siteLocale) },
      { property: 'og:image', content: ogImageUrl.value },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitleBase.value },
      { name: 'twitter:image', content: ogImageUrl.value },
    ],
    link: links,
  };
});

function formatDate(dateString: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const dateLocale = {
    en: 'en-US',
    ja: 'ja-JP',
    ko: 'ko-KR',
    es: 'es-ES',
  }[siteLocale];
  return date.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

type BlogFaqItem = {
  question: string;
  answer: string;
};

const faqItems = computed(() => {
  const raw = i18n.getRaw('landingPages.blog.index.faq.items');
  return Array.isArray(raw) ? (raw as BlogFaqItem[]) : [];
});

const faqSectionTitle = computed(() => t('landingPages.blog.index.faq.title'));

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="min-h-screen bg-white">
    <AppNav :active-path="blogIndexPath" />

    <section
      class="relative pt-48 pb-32 overflow-hidden learn-hero"
      :aria-label="t('ui.a11y.heroSection')"
    >
      <div class="relative container mx-auto px-6 md:px-4 text-center z-10">
        <h1
          class="font-boldonse text-5xl md:text-7xl text-white uppercase tracking-tight mb-8 drop-shadow-sm"
        >
          {{ heroTitle }}
        </h1>
        <p class="font-boldonse text-white/90 text-xl max-w-2xl mx-auto uppercase">
          {{ heroSubtitle }}
        </p>
      </div>
    </section>

    <main class="relative z-[1]">
      <section
        class="max-w-[1200px] mx-auto px-4 md:px-8 pt-24 pb-24"
        :aria-label="t('ui.a11y.blogPosts')"
      >
        <div
          v-if="paginatedPosts && Array.isArray(paginatedPosts) && paginatedPosts.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <NuxtLink
            v-for="post in paginatedPosts"
            :key="post._id"
            :to="blogPostPath(post.slug)"
            class="flex flex-col bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02] shadow-[0_3px_7px_0px_rgba(0,0,0,0.05)]"
          >
            <div v-if="post?.image" class="h-48 relative overflow-hidden bg-black/5">
              <img
                :src="post.image"
                :alt="post?.title || t('landingPages.blog.index.image_alt_fallback')"
                class="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                loading="lazy"
              />
            </div>

            <div class="flex-1 flex flex-col p-6">
              <div class="flex items-center gap-3 mb-3">
                <span
                  v-if="
                    post?.category ||
                    (post?.categories &&
                      Array.isArray(post.categories) &&
                      post.categories.length > 0)
                  "
                  class="text-xs font-medium px-3 py-1 rounded-full bg-black/5 text-black/60"
                >
                  {{ post.category || post.categories?.[0]?.title }}
                </span>
                <span v-if="post?.readTime" class="text-xs text-black/50">
                  {{ t('landingPages.blog.index.min_read', { count: post.readTime }) }}
                </span>
              </div>

              <h3 class="text-xl md:text-2xl font-normal text-black/90 mb-3 line-clamp-2">
                {{ post?.title }}
              </h3>

              <p
                v-if="post?.excerpt"
                class="text-base text-black/60 font-normal leading-relaxed mb-4 line-clamp-3"
              >
                {{
                  Array.isArray(post.excerpt)
                    ? post.excerpt
                        .map((block: any) => {
                          if (block._type === 'block' && block.children) {
                            return block.children
                              .map((child: any) => {
                                if (typeof child === 'string') return child;
                                if (child.text) return child.text;
                                return '';
                              })
                              .join('');
                          }
                          return '';
                        })
                        .join(' ')
                        .trim()
                    : post.excerpt
                }}
              </p>

              <div class="mt-auto pt-4 flex items-center justify-between">
                <div v-if="post?.author" class="flex items-center gap-2">
                  <img
                    v-if="post.author?.avatar"
                    :src="post.author.avatar"
                    :alt="post.author?.name || t('landingPages.blog.index.author_alt_fallback')"
                    class="w-8 h-8 rounded-full"
                  />
                  <span class="text-sm text-black/70">
                    {{ post.author?.name }}
                  </span>
                </div>
                <span v-if="post?.publishedAt" class="text-sm text-black/50">
                  {{ formatDate(post.publishedAt) }}
                </span>
              </div>

              <div class="mt-4 pt-4 border-t border-black/5">
                <span class="text-sm font-medium text-black/80 inline-flex items-center gap-2">
                  {{ t('landingPages.blog.index.card.read_more') }}
                  <span class="transition-transform group-hover:translate-x-1">→</span>
                </span>
              </div>
            </div>
          </NuxtLink>
        </div>

        <div v-else class="text-center py-24">
          <p class="text-lg text-black/60">{{ t('landingPages.blog.index.empty_state') }}</p>
        </div>

        <div
          v-if="totalPages > 1"
          class="flex items-center justify-center gap-2 mt-12"
          :aria-label="t('ui.a11y.pagination')"
        >
          <button
            @click="prevPage"
            :disabled="currentPage === 1"
            :aria-label="t('landingPages.blog.index.pagination.prev_aria')"
            class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
          >
            {{ t('landingPages.blog.index.pagination.prev') }}
          </button>

          <div class="flex items-center gap-1">
            <button
              v-for="(pageNum, index) in pageNumbers"
              :key="`page-${index}`"
              @click="typeof pageNum === 'number' ? goToPage(pageNum) : null"
              :disabled="typeof pageNum === 'string'"
              :aria-label="
                typeof pageNum === 'number'
                  ? t('ui.a11y.goToPage', { page: String(pageNum) })
                  : undefined
              "
              :aria-current="pageNum === currentPage ? 'page' : undefined"
              class="min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:cursor-default"
              :class="
                pageNum === currentPage
                  ? 'bg-black/90 text-white'
                  : typeof pageNum === 'string'
                    ? 'text-black/30 cursor-default'
                    : 'bg-white text-black/70 hover:bg-black/5'
              "
            >
              {{ pageNum }}
            </button>
          </div>

          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            :aria-label="t('landingPages.blog.index.pagination.next_aria')"
            class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
          >
            {{ t('landingPages.blog.index.pagination.next') }}
          </button>
        </div>

        <div v-if="totalPosts > 0" class="text-center mt-6 text-sm text-black/50">
          {{
            t('landingPages.blog.index.showing_posts', {
              start: startIndex + 1,
              end: Math.min(endIndex, totalPosts),
              total: totalPosts,
            })
          }}
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="container mx-auto px-6 md:px-4">
          <div class="max-w-[800px] mx-auto">
            <h2
              class="font-boldonse text-[clamp(1.5rem,3vw,2rem)] text-black/90 uppercase leading-[1.6] mb-12 text-center"
            >
              {{ faqSectionTitle }}
            </h2>
            <div class="space-y-4">
              <div
                v-for="(item, index) in faqItems"
                :key="index"
                class="bg-black/[0.03] rounded-3xl px-8 py-6 transition-all duration-300"
              >
                <button
                  type="button"
                  @click="toggleFaq(index)"
                  class="flex items-center justify-between w-full text-left font-medium text-xl text-black/90 focus:outline-none"
                >
                  <h3 class="font-medium text-xl text-black/90">{{ item.question }}</h3>
                  <span
                    class="transform transition-transform duration-300 text-2xl"
                    :class="{ 'rotate-45': expandedFaqIndex === index }"
                  >
                    +
                  </span>
                </button>
                <div
                  class="grid transition-all duration-300 ease-in-out overflow-hidden"
                  :class="
                    expandedFaqIndex === index
                      ? 'grid-rows-[1fr] opacity-100 mt-4'
                      : 'grid-rows-[0fr] opacity-0'
                  "
                >
                  <div class="overflow-hidden">
                    <p class="text-black/60 leading-relaxed text-lg">
                      {{ item.answer }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FeaturesRoles />
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
.learn-hero {
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
}

.learn-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}
</style>
